import React from 'react'
import { XMarkIcon, CheckCircleIcon, PrinterIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { getEcddPreviewSections } from './ecddPreviewContent'
import { printEcddPreview } from '../../../utils/ecddPreviewPrint'

import { formatSydneyDateTime } from '../../../utils/sydneyDateTime'
function Row({ label, value }) {
  if (value == null || value === '') return null
  return (
    <div className="grid grid-cols-[140px_1fr] sm:grid-cols-[180px_1fr] gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-0.5">{label}</div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{value}</div>
    </div>
  )
}

function Section({ number, title, ok, children }) {
  return (
    <section className="mb-5 last:mb-0">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-psg-teal text-white text-[11px] font-bold flex items-center justify-center shrink-0">
          {number}
        </div>
        <h3 className="text-sm font-bold text-[#073B4D] m-0 flex-1">{title}</h3>
        {ok != null && (
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
              ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
            }`}
          >
            {ok ? 'Complete' : 'Incomplete'}
          </span>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 sm:ml-8">{children}</div>
    </section>
  )
}

/**
 * Read-only ECDD preview — selections and answers only (not live form fields).
 */
function FormBlock({ formName, submittedAt, submissionRef, rows }) {
  const meta = [
    submissionRef ? `Ref ${submissionRef}` : null,
    submittedAt ? formatSydneyDateTime(new Date(submittedAt)) : null,
  ].filter(Boolean).join(' · ')
  return (
    <div className="mb-4 last:mb-0 rounded-lg border border-teal-100 bg-white px-3 py-2.5">
      <div className="text-sm font-semibold text-teal-900">{formName}</div>
      {meta ? <div className="text-[11px] text-slate-500 mb-2">{meta}</div> : null}
      {rows.map((r) => (
        <Row key={`${formName}-${r.label}`} label={r.label} value={r.value} />
      ))}
    </div>
  )
}

export default function EcddDocumentPreviewModal({
  open,
  onClose,
  record,
  formAnswers,
  decision,
  decisionRationale,
  gates,
  dynamicFormResponses = [],
}) {
  if (!open) return null

  const { sections, evalGates } = getEcddPreviewSections({
    record,
    formAnswers,
    decision,
    decisionRationale,
    gates,
    dynamicFormResponses,
  })

  const handlePrint = () => {
    printEcddPreview({ record, formAnswers, decision, decisionRationale, gates, dynamicFormResponses })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-slate-200">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">ECDD preview</div>
            <h2 className="text-lg font-bold text-[#073B4D] m-0 mt-0.5 truncate">
              {record?.name || 'Patron'} · {record?.id || '—'}
            </h2>
            <p className="text-xs text-slate-500 m-0 mt-1">
              Read-only summary of recorded selections — not the working form.
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
              onClick={handlePrint}
              title="Print preview"
            >
              <PrinterIcon className="w-4 h-4 mr-1" />
              Print
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
              aria-label="Close preview"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {sections.map((section) => (
            <Section key={section.number} number={section.number} title={section.title} ok={section.ok}>
              {section.forms?.length
                ? section.forms.map((form) => (
                  <FormBlock
                    key={`${form.formName}-${form.submissionRef || form.submittedAt}`}
                    formName={form.formName}
                    submittedAt={form.submittedAt}
                    submissionRef={form.submissionRef}
                    rows={form.rows || []}
                  />
                ))
                : section.rows.map((r) => (
                  <Row key={r.label} label={r.label} value={r.value} />
                ))}
            </Section>
          ))}

          {evalGates.allOk ? (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
              <CheckCircleIcon className="w-4 h-4 shrink-0" />
              All required form sections are complete.
            </div>
          ) : (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              Some required sections are still incomplete — see badges above.
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 shrink-0 bg-slate-50/80 rounded-b-xl">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
            onClick={handlePrint}
          >
            <PrinterIcon className="w-4 h-4 mr-1" />
            Print
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close preview
          </Button>
        </div>
      </div>
    </div>
  )
}
